import React, { useState, useEffect } from "react";


export default function AdminPage() {
  const [orders, setOrders] = useState([]);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [newOrder, setNewOrder] = useState({ email: "", reference: "", sku: "" });
  const [skuOptions, setSkuOptions] = useState({});
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");

  // load admin emails when logged in
  useEffect(() => {
    if (token) {
      fetchAdminEmails();
    }
  }, [token]);

  useEffect(() => {
  if (Object.keys(skuOptions).length > 0) {
    setNewOrder(order => ({
      ...order,
      sku: Object.keys(skuOptions)[0]
      }));
    }
  }, [skuOptions]);

  const fetchAdminEmails = async () => {
    try {
      const res = await fetch("/api/admin/emails", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setAdminEmails(data.admins);
    } catch (err) {
      console.error("Failed to fetch admin emails", err);
    }
  };

  const addAdminEmail = async () => {
    if (!newAdminEmail) return;
    try {
      const res = await fetch("/api/admin/emails", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: newAdminEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setNewAdminEmail("");
        fetchAdminEmails();
      } else {
        alert(data.error || "Failed to add email");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to add admin email");
    }
  };

  const deleteAdminEmail = async (id) => {
    if (!window.confirm("Remove this admin email?")) return;
    try {
      await fetch(`/api/admin/emails/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAdminEmails();
    } catch (err) {
      console.error(err);
      alert("Failed to delete admin email");
    }
  };

  // Load orders on login
  useEffect(() => {
    if (token) {
      loadOrders();
      fetchSkuMapping();
    }
  }, [token]);
  
  // --- Fetch SkuMapping ---
  const fetchSkuMapping = async () => {
  try {
    const res = await fetch("/api/admin/sku-mapping", {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.success) setSkuOptions(data.skuMapping);
  } catch (err) {
    console.error("Failed to load SKU mapping", err);
    }
  };
  // --- Login ---
  const login = async () => {
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem("adminToken", data.token);
        setToken(data.token);
      } else {
        alert(data.message || "Wrong password");
      }
    } catch (err) {
      console.error(err);
      alert("Login failed. Is the backend running?");
    }
  };

  // --- Logout ---
  const logout = () => {
    localStorage.removeItem("adminToken");
    setToken("");
    setOrders([]);
  };

  // --- Load Orders ---
  const loadOrders = async () => {
    try {
      const res = await fetch("/api/admin/orders", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        alert("Session expired");
        logout();
        return;
      }
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      console.error(err);
      alert("Failed to load orders");
    }
  };

  // --- Delete Order ---
  const deleteOrder = async (id) => {
    try {
      await fetch(`/api/admin/orders/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      loadOrders();
    } catch (err) {
      console.error(err);
      alert("Failed to delete order");
    }
  };

  // --- Create Order ---
  const createOrder = async () => {
    if (!newOrder.email || !newOrder.reference || !newOrder.sku) {
      alert("Please fill all fields");
      return;
    }
    try {
      const res = await fetch("/api/admin/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newOrder),
      });
      const data = await res.json();
      if (data.success) {
        alert("Order created!");
        loadOrders();
        setNewOrder({ email: "", reference: "", sku: "1" });
      } else {
        alert(data.error || "Error creating order");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to create order");
    }
  };

  // --- Render login form ---
  if (!token) return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">Admin Login</h2>
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        className="border p-2 w-full mb-4"
      />
      <button
        onClick={login}
        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
      >
        Login
      </button>
    </div>
  );

  // --- Render admin dashboard ---
  
  return (
    <div className="max-w-4xl mx-auto mt-10 p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-bold mb-4">Admin Dashboard</h2>
      <button
        onClick={logout}
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 mb-6"
      >
        Logout
      </button>

      {/* --- Admin Emails Management --- */}
      <hr className="my-6"/>
      <h3 className="text-xl font-semibold mb-2">Admin Emails</h3>
      <ul className="mb-3">
        {adminEmails.map(a => (
          <li key={a.id} className="flex justify-between items-center border p-2 rounded mb-2">
            {a.email}
            <button
              onClick={() => deleteAdminEmail(a.id)}
              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mb-6">
        <input
          type="email"
          placeholder="New admin email"
          value={newAdminEmail}
          onChange={e => setNewAdminEmail(e.target.value)}
          className="border p-2 flex-1"
        />
        <button
          onClick={addAdminEmail}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Add
        </button>
      </div>
      {/* --- End Admin Emails --- */}

      <h3 className="text-xl font-semibold mb-2">Create Manual Order</h3>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          placeholder="Email"
          value={newOrder.email}
          onChange={e => setNewOrder({ ...newOrder, email: e.target.value })}
          className="border p-2 flex-1 min-w-[180px]"
        />
        <input
          placeholder="Reference"
          value={newOrder.reference}
          onChange={e => setNewOrder({ ...newOrder, reference: e.target.value })}
          className="border p-2 flex-1 min-w-[180px]"
        />
        <select
          value={newOrder.sku}
          onChange={(e) => setNewOrder({ ...newOrder, sku: e.target.value })}
          className="border p-2 w-50"
        >
        {Object.keys(skuOptions).length === 0 ? (
          <option>Loading...</option>
        ) : (
          Object.keys(skuOptions).map((sku) => (
            <option key={sku} value={sku}>
              SKU {sku} ({skuOptions[sku]} images)
            </option>
          ))
        )}
        </select>
        <button
          onClick={createOrder}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Create
        </button>
      </div>

      <hr className="my-4"/>
      <ul className="space-y-4">
        {orders.map(o => (
          <li key={o.id} className="border p-4 rounded shadow bg-gray-50">
            <p><b>{o.reference}</b> ({o.email}) - SKU: {o.sku}</p>
            <p>
              Upload URL: <a href={o.uploadUrl} target="_blank" rel="noreferrer" className="text-blue-600">{o.uploadUrl}</a>
            </p>
            <p className="flex items-center gap-2">
              Submitted: 
              <span className={o.submitted ? "text-green-600" : "text-red-600"}>
                {o.submitted ? "Yes" : "No"}
              </span>
              <button
                onClick={async () => {
                  if (!window.confirm("Are you sure you want to reset this order's submitted status?")) return;
                  try {
                    const res = await fetch(`/api/admin/orders/${o.id}/reset-submitted`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const data = await res.json();
                    if (data.success) {
                      alert(data.message);
                      loadOrders(); // refresh list
                    } else {
                      alert(data.error || "Failed to reset");
                    }
                  } catch (err) {
                    console.error(err);
                    alert("Failed to reset submitted status");
                  }
                }}
                disabled={!o.submitted} // disable if not submitted
                className={`px-2 py-1 rounded text-white ${
                  o.submitted 
                    ? "bg-yellow-500 hover:bg-yellow-600" 
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Reset
              </button>
            </p>
            <img src={o.qrCode} alt="QR" width="80" className="my-2"/>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button
                onClick={() => deleteOrder(o.id)}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/admin/orders/${o.id}/download`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (!res.ok) throw new Error("Failed to download");
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${o.reference}-images.zip`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                   console.error(err);
                   alert("Download failed");
                  }
                }}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Download Images
              </button>
              {/* --- Download Print Sheet Button --- */}
              <button
                onClick={async () => {
                try {
                  const res = await fetch(`/api/admin/orders/${o.id}/print`, {
                  headers: { Authorization: `Bearer ${token}` }
                  });
                  if (!res.ok) throw new Error("Failed to generate print sheet");
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${o.reference}-print.png`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error(err);
                  alert("Failed to download print sheet");
                }
              }}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Download Print Sheet
            </button>
          </div>
        </li>
      ))}
    </ul>
  </div>
  );
}
